<template>
    <div class="page">
        <h1>Shrimp Selection</h1>
        <p>Choose some shrimp species for your tank.</p>

        <div class="button-container">
            <button @click="deselectAll">Deselect all</button>
        </div>

        <ul>
            <li v-for="shrimp in searchResults" v-bind:key="shrimp.id" :class="{'selected': isShrimpSelected(shrimp.id)}">
                <input type="checkbox" v-model="selectedShrimpIds" :value="shrimp.id" :id="shrimp.id" @change="selectedShrimpChange(shrimp.id)"/>
                <label :for="shrimp.id">
                    <img :src="getImagePathForShrimp(shrimp)" @error="imageFailure" :alt="shrimp.name" />
                    {{ shrimp.name }}
                </label>
            </li>
        </ul>

        <div class="sticky-bottom">
            <div class="search-bar">
                <input type="search" name="shrimp-search" v-on:input="searchQuery = $event.target.value" placeholder="Search" class="search">
            </div>
            <div class="button-container">
                <button :disabled="!confirmButtonEnabled" @click.prevent="confirmShrimp" class="button primary">{{ confirmShrimpButtonLabel }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Fuse from 'fuse.js'
import debounce from 'lodash/debounce'
import * as ANALYTICS from '@/lib/analytics'

let searchOptions = {
    shouldSort: true,
    threshold: 0.4,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    minMatchCharLength: 1,
    keys: [
        'id',
        'name',
    ]
}

export default {
    name: 'ShrimpSelector',
    data() {
        let fuse = new Fuse(this.$store.state.allShrimp, searchOptions)

        return {
            selectedShrimpIds: [],
            fuse: fuse,
            searchQuery: '',
            searchResults: this.$store.state.allShrimp,
        }
    },
    mounted() {
        this.selectedShrimpIds = this.$store.state.selectedShrimpIds
    },
    methods: {
        doSearch(query) {
            let results = this.$store.state.allShrimp

            if(query) {
                this.$ga.event(ANALYTICS.CATEGORY, 'Shrimp search', query)
                results = this.fuse.search(query)
            }

            this.searchResults = results
        },
        selectedShrimpChange(shrimpId) {
            if(this.searchQuery && this.isShrimpSelected(shrimpId)) {
                let labelObj = {
                    q: this.searchQuery,
                    shrimp: shrimpId
                }
                this.$ga.event(ANALYTICS.CATEGORY, 'Shrimp select with search query', JSON.stringify(labelObj))
            }
        },
        deselectAll() {
            this.$ga.event(ANALYTICS.CATEGORY, 'Shrimp selector deselect all', this.selectedShrimpIds.length.toString())
            this.selectedShrimpIds = []
        },
        confirmShrimp() {
            this.$ga.event(ANALYTICS.CATEGORY, 'Selected shrimp count', this.selectedShrimpIds.length.toString())
            this.$ga.event(ANALYTICS.CATEGORY, 'Selected shrimp', this.selectedShrimpIds.toString())
            this.$store.commit('SET_SELECTED_SHRIMP_IDS', this.selectedShrimpIds)
            this.$router.push(this.$store.getters['getSummaryUri'])
        },
        isShrimpSelected(shrimpId) {
            return this.selectedShrimpIds.includes(shrimpId)
        },
        getImagePathForShrimp(shrimp) {
            return this.$store.getters['shrimpImageUri'](shrimp.id)
        },
        imageFailure() {
            let defaultImageUri = this.$store.getters['defaultImageUri']

            if(event.target.src === defaultImageUri) {
                // Prevent getting stuck in a loop if the default image fails to load
                return
            }

            event.target.src = defaultImageUri
        }
    },
    computed: {
        confirmButtonEnabled() {
            return this.selectedShrimpIds.length > 0
        },
        confirmShrimpButtonLabel() {
            if(this.selectedShrimpIds.length > 1) {
                return "Select " + this.selectedShrimpIds.length + " shrimp"
            }

            return "Select shrimp"
        }
    },
    watch: {
        searchQuery: debounce(function(newVal) {
            this.doSearch(newVal)
        }, 300)
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/size";
@import "@/assets/scss/mixins";

$bottom-bar-height: 10em;
$bottom-bar-height-tablet: 7em;

.page {
    // Ensure all content can be seen
    margin-bottom: $bottom-bar-height + 1em;

    @include breakpoint(tablet) {
        margin-bottom: $bottom-bar-height-tablet + 1em;
    }
}

ul {
    margin: 0;
    padding: 1em;
    background: #e1e8e0;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    align-items: flex-start;

    li {
        list-style-type: none;
        width: 40%;
        background: white;
        display: flex;
        flex-direction: column;
        margin-bottom: 1em;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(0, 0, 0, 0.2);

        &.selected {
            animation: small-growth 0.2s;
            animation-timing-function: ease-in-out;
        }

        @include breakpoint(tablet) {
            width: 30%;
        }

        label {
            flex: 1;
            border-bottom: 4px solid transparent;
            padding: 1em;
            font-weight: bold;
        }

        img {
            max-width: 100%;
            object-fit: contain;
            margin: 0.5em;
        }

        input[type="checkbox"] {
            position: absolute;
            opacity: 0;

            &:checked {
                + label {
                    background-color: #F3FCE8;
                    border-bottom: 4px solid #84CC8E;
                }
            }

            &:focus {
                + label {
                    outline: 1px solid rgb(93, 133, 207);
                }
            }
        }
    }
}

.sticky-bottom {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: $bottom-bar-height;
    width: 100%;
    max-width: $max-page-width;
    padding: 1em;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-top: 1px solid rgba(0, 0, 0, 0.2);
    background: #fff;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);

    @include breakpoint(tablet) {
        flex-direction: row;
        justify-content: center;
        height: $bottom-bar-height-tablet;
        padding: 1.5em;
    }

    .search-bar {
        flex: 2;
        display: flex;
        justify-content: center;
        align-items: center;

        @include breakpoint(tablet) {
            margin-right: 1.5em;
        }
    }

    .search {
        font-size: 1.2em;
        padding: 1em;
        width: 100%;

        @include breakpoint(tablet) {
            height: 100%;
        }
    }

    .button-container {
        margin: 0;
        flex: 1;
        align-items: center;
        justify-content: center;

        .button {
            width: 100%;

            @include breakpoint(tablet) {
                height: 100%;
            }
        }
    }
}

.button-container {
    display: flex;
    margin-bottom: 1.5em;
}

@keyframes small-growth {
    from {
        transform: scale(1);
    }
    35% {
        transform: scale(1.05);
    }
    to {
        transform: scale(1);
    }
}
</style>